/**
 * Jendela lembar log LRLC — enam belas kolom bawaan, satu sumur, satu pertanyaan.
 *
 * Layar LRLC memulangkan angka: berapa meter tiap kelas, selang mana yang
 * paling tebal, awan RT–PHIE. Tidak satu pun dari ketiganya menjawab apa yang
 * sebenarnya ditanyakan orang di depannya — *di kedalaman mana, dan apa yang
 * terbaca di sebelahnya*. Itu pertanyaan lembar log, dan sebuah tabel tidak
 * bisa menjawabnya betapapun banyak barisnya.
 *
 * Tiga hal yang membedakannya dari lembar di menu Log, dan ketiganya disengaja.
 *
 * **Susunan bawaannya adalah sebuah alasan, bukan sebuah selera.** Keenam belas
 * kolomnya berjalan dari yang paling mudah salah tera ke yang paling sulit,
 * sebab yang ditanyakan di sini bukan "seperti apa sumur ini" melainkan
 * "apakah penggolongan tadi masuk akal" — dan sebuah lembar yang menaruh kolom
 * kelas paling kiri meminta pembacanya percaya sebelum melihat. Susunan itu
 * yang ditemui siapa pun yang membuka lembar ini pertama kali. Ia tetap bisa
 * disusun ulang, dibuang, dan ditambah sesudahnya — enam belas kolom pada layar
 * selebar 1.366 piksel adalah enam belas pita sempit, dan yang sedang memeriksa
 * satu selang setebal empat meter kerap hanya memerlukan lima di antaranya
 * berdampingan. Yang tersimpan adalah selisihnya, dan susunan bawaan selalu
 * satu tombol jauhnya di kaki rel kanan.
 *
 * **Rw dan ambang RT/RO bisa diketik dari dalam sini**, dan keduanya bukan
 * setelan tampilan: lembarnya dihitung ulang. Keduanya milik **sumur ini** dan
 * bukan milik proyek — per seluruh kedalaman, per selang marker, atau per zona
 * — sebab air formasi berubah menurut tempat, dan di sumur yang menembus tiga
 * akuifer ia berubah menurut kedalaman juga. Lihat `LrlcWaterPlanPanel`, dan
 * `petro/waterplan.py` untuk sebabnya. Rw proyek tetap tinggal di rel kiri
 * layar LRLC dan tetap menjadi yang dibaca sumur yang belum menyebutkan
 * miliknya sendiri; panel di sini memajang angka itu sebagai bayangan di
 * dalam kotak yang kosong, supaya tidak ada yang tersembunyi.
 *
 * **Yang tergambar dihitung, bukan dibaca dari yang tersimpan.** Kolom kelas
 * di lembar menu Log menggambar `LRLC_CLASS` yang sudah diterbitkan, dan itu
 * benar di sana. Di sini justru sebaliknya: layar ini tempat ambangnya
 * digeser, dan sebuah lembar yang baru berubah sesudah "Publish" memaksa orang
 * menerbitkan belasan hasil yang tak satu pun dimaksudkan disimpan.
 *
 * **Satu sumur pada satu saat.** Enam belas kolom sudah memenuhi lebar layar;
 * dua sumur berdampingan berarti tiga puluh dua, dan tak satu pun terbaca.
 * Yang ingin membandingkan sumur memakai layar Correlation, yang memang
 * dibangun untuk itu.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { canCompute } from "../auth";
import {
  BoxZoomOverlay,
  DEPTH_GUTTER_W,
  DepthGutter,
  LogTrack,
  TrackHeaders,
  sampleAt,
  tracksMinWidth,
  type TrackSpec,
} from "./LogTracks";
import { Button, Dropdown, Empty, Loading, Notice, Panel, Slider, Toggle, useToast } from "./ui";
import LrlcWaterPlanPanel, { type WaterPlanPatch } from "./LrlcWaterPlanPanel";
import { MarkerHandles } from "./MarkerTools";
import { TrackEditor } from "./TrackEditor";
import { Icon } from "./icons";
import { useAsync, useStored } from "../hooks";
import { DEPTH_HINT, DEPTH_LABEL, DEPTH_ORDER, axisSpan } from "../lib/depthaxis";
import { DEPTH_SCALE_PRESETS, depthTickCount, stepScale } from "../lib/depthscale";
import { useDepthSheet } from "../lib/depthsheet";
import {
  applyOverrides,
  bandsFor,
  buildLrlcTracks,
  defaultLayout,
  diffOverrides,
  emptyBandNote,
  isDefaultLayout,
  layoutTracks,
  moveEntryTo,
  readLayout,
  readOverrides,
  resolveRoles,
  restorableTracks,
  restoreEntry,
  sheetRange,
  toLayout,
  toTrackConfigs,
  type BandKind,
  type SheetEntry,
  type SheetOverrides,
} from "../lib/lrlcsheet";
import { unitsFor } from "../lib/units";
import type { CurveConfig, SpectrumConfig, TrackConfig } from "../lib/tracklayout";
import type {
  DepthReference,
  LrlcSheetPayload,
  Project,
} from "../types";

/**
 * Apa yang diminta lembar ini dari server, di luar sumurnya.
 *
 * Dipegang oleh layar LRLC dan diserahkan apa adanya: kurva yang dibaca dan
 * ambang yang berlaku adalah **yang sedang terpasang di rel kiri**, bukan yang
 * terakhir dijalankan. Sebuah lembar yang menggambar setelan lain daripada
 * yang terpampang di sebelahnya adalah lembar yang berbohong tanpa satu pun
 * tanda di permukaannya.
 */
export interface LrlcSheetRequest {
  curves: Record<string, string>;
  cutoffs: Record<string, number>;
  archie: Record<string, number | null>;
  zone: string;
}

export default function LrlcLogSheet({
  project,
  wells,
  request,
  wellId: asked,
  onWellChange,
  onWaterChanged,
  onClose,
}: {
  project: Project;
  /** Sumur yang benar-benar bisa dihitung — yang belum dibor tidak punya kurva. */
  wells: { id: number; name: string; color: string }[];
  request: LrlcSheetRequest;
  /**
   * Sumur yang diminta dari luar — yang tertulis di `?sheet=`.
   *
   * Dahulu ia bernama `initialWell` dan memang hanya dibaca sekali. Sekarang
   * lembar ini mengikutinya juga ketika ia berpindah sesudah terbuka, sebab
   * pemilih WELL di cangkang bisa memindahkannya: lembar yang tetap menggambar
   * sumur kemarin sementara alamat dan pemilih di atas sudah menyebut sumur
   * yang lain adalah tiga tempat yang mengatakan dua hal.
   */
  wellId?: number;
  /**
   * Sumur yang digambar lembar ini berpindah, lewat pemilih di kepalanya.
   *
   * Dikatakan ke luar sebab lembar ini dibuka lewat alamat — `?sheet=12` — dan
   * alamat yang tertinggal pada sumur yang tadi adalah tautan yang membuka
   * lembar sumur yang lain daripada yang sedang dibaca pengirimnya. Layar di
   * belakangnya yang meneruskannya ke pemilih WELL di cangkang.
   */
  onWellChange?: (wellId: number) => void;
  /**
   * Rw atau ambang RT/RO sebuah sumur barusan berpindah.
   *
   * Lembar ini menggambar ulang dirinya sendiri, dan itu cukup untuk lembarnya
   * — tetapi tabel dan ringkasan di layar di belakangnya lahir dari
   * penggolongan yang sudah berjalan, dan penggolongan itu tidak ikut
   * berjalan lagi. Dikatakan ke luar supaya layarnya bisa mengatakan bahwa
   * angkanya sudah berubah, alih-alih memajang tebal yang dihitung dengan Rw
   * yang tidak lagi terpasang di mana pun.
   */
  onWaterChanged?: (wellName: string) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const units = unitsFor(project.unit_system);

  const [wellId, setWellId] = useState<number>(asked ?? wells[0]?.id ?? 0);
  useEffect(() => {
    if (!wells.some((w) => w.id === wellId)) setWellId(wells[0]?.id ?? 0);
  }, [wells, wellId]);

  /**
   * Sumur yang terakhir diterima dari luar, atau yang terakhir dikabarkan.
   *
   * Sepasang efek di bawah menyeberangkan perpindahan ke arah yang berlawanan,
   * dan tanpa penanda ini keduanya saling memantulkan. Perbandingan yang lebih
   * jelas — `wellId !== asked` — tidak cukup, sebab di antara alamat yang sudah
   * berpindah dan lembar yang belum menyusulnya ada satu render tempat keduanya
   * memang berbeda: efek pengabar membacanya sebagai perpindahan yang lahir di
   * sini, mengirim sumur yang lama kembali ke alamat, dan keduanya lalu
   * bertukar tempat tanpa henti. Yang dicatat di sini adalah *asal* perpindahan
   * terakhir, dan hanya yang tidak berasal dari luar yang dikabarkan keluar.
   */
  const settled = useRef(asked ?? wellId);

  /* Yang diminta dari luar berpindah — pemilih WELL di cangkang bergerak, atau
     sebuah tautan dibuka — dan lembar ini mengikutinya. */
  useEffect(() => {
    if (!asked || asked === wellId) return;
    settled.current = asked;
    setWellId(asked);
    // `wellId` sengaja tidak ikut: yang dijawab efek ini adalah perpindahan
    // permintaan dari luar, bukan perpindahan yang lahir di dalam lembar ini.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked]);

  /* Dan sebaliknya: pemilih di kepala lembar berpindah — atau sumur yang
     tergambar hilang dari daftarnya — dan yang di luar dikabarkan supaya alamat
     serta pemilih di cangkang menyusul.

     Handler-nya dibaca lewat ref dan **tidak** ikut ke dalam dependensi.
     `onWellChange` yang diserahkan layar di belakangnya adalah fungsi baru pada
     tiap render; sebagai dependensi ia membuat efek ini berjalan pada tiap
     render, dan tiap putaran menulis ulang alamat yang melahirkan render
     berikutnya. */
  const tell = useRef(onWellChange);
  tell.current = onWellChange;
  useEffect(() => {
    if (!wellId || wellId === settled.current) return;
    settled.current = wellId;
    tell.current?.(wellId);
  }, [wellId]);

  // Kunci tersendiri, bukan kunci lembar di menu Log. Keduanya lembar yang
  // berbeda dengan lebar kolom yang berbeda, dan skala 1:200 yang pas untuk
  // yang satu memampatkan yang lain sampai tak terbaca — sebuah setelan
  // bersama berarti tiap perpindahan layar merusak yang ditinggalkan.
  const [depthMode, setDepthMode] = useStored<DepthReference>(
    `evolog.lrlc.depth.${project.id}`, project.default_depth_reference ?? "md");
  const [showGrid, setShowGrid] = useStored("evolog.lrlc.grid", true);
  // Menyala sejak awal: penanda stratigrafi adalah tempat lembar ini
  // membandingkan dirinya dengan apa yang sudah diketahui orang tentang
  // sumurnya, dan sebuah kelas LRLC yang berhenti tepat di sebuah top
  // mengatakan hal yang berbeda daripada yang berhenti di tengah lapisan.
  // Bisa dipadamkan sebab pada sumur berpenanda dua puluh — Jirak — garis
  // putus-putusnya memenuhi lembar dan kurvanya yang justru tak terbaca.
  const [showMarkers, setShowMarkers] = useStored("evolog.lrlc.markers", true);
  const [showReadings, setShowReadings] = useStored("evolog.lrlc.readings", false);
  const [gridDivisions, setGridDivisions] = useStored("evolog.lrlc.griddiv", 4);
  const [depthGrid, setDepthGrid] = useStored("evolog.lrlc.depthgrid", 24);
  const [weight, setWeight] = useStored("evolog.lrlc.weight", 1.2);
  // Kunci yang sama seperti sebelumnya, dan `readOverrides` membaca bentuk
  // lamanya: yang dulu tersimpan hanyalah kedua ujung skala tiap kurva, dan
  // skala yang sudah disetel orang tidak boleh hilang hanya karena versi ini
  // bisa menyetel warna juga.
  const [stored, setStored] = useStored<unknown>("evolog.lrlc.curvescale", {});
  const overrides: SheetOverrides = useMemo(() => readOverrides(stored), [stored]);
  /**
   * Susunan kolomnya: apa yang ada, dan di urutan mana.
   *
   * Tersimpan terpisah dari setelan tampilan di atas dan bukan di dalamnya,
   * sebab keduanya menjawab hal yang berbeda — yang satu apa dan di mana, yang
   * lain bagaimana digambar — dan "Reset display" hanya boleh membatalkan yang
   * kedua. Yang belum pernah menyusun kolomnya menyimpan `null`, yang dibaca
   * sebagai susunan bawaan `LRLC_SHEET`.
   */
  const [storedLayout, setStoredLayout] = useStored<unknown>("evolog.lrlc.layout", null);
  const layout = useMemo<SheetEntry[] | null>(() => readLayout(storedLayout), [storedLayout]);
  const sheetTracks = useMemo(() => layoutTracks(layout), [layout]);

  /** Track yang panel setelannya sedang terbuka di rel kanan. */
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);
  const [railOpen, setRailOpen] = useStored("evolog.lrlc.rail", false);

  const [cursor, setCursor] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  /**
   * Di mana jari berada, dan kolom mana yang sedang ditunjuknya.
   *
   * Dipegang dalam koordinat layar dan bukan koordinat lembar: kotak pembacanya
   * digambar `position: fixed` supaya tidak terpotong kolom selebar 60 piksel
   * yang memuatnya — kolom lembar ini menggunting isinya (`overflow: hidden`),
   * dan sebuah kotak yang setengahnya hilang di tepi kolom lebih buruk
   * daripada tidak ada kotaknya.
   */
  const [pointer, setPointer] = useState<{ x: number; y: number; track: string } | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => setPinned(null), [wellId, depthMode]);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  // Ambang dan Archie masuk ke dalam kunci `useAsync` sebagai teks, supaya
  // menggeser satu penggeser di rel kiri benar-benar menggambar ulang lembar
  // ini. Tanpa itu ia akan memajang hasil ambang yang lama di sebelah angka
  // ambang yang baru, dan tidak ada apa pun di layar yang mengatakannya.
  const signature = JSON.stringify([request.curves, request.cutoffs,
                                    request.archie, request.zone]);

  /**
   * Naik satu tiap kali rencana air sumur ini disimpan.
   *
   * Ada di dalam kunci `useAsync` sebab rencana itu hidup di server dan bukan
   * di dalam `request`: tanpa ini, menyimpan Rw baru memulangkan lembar yang
   * sama persis dari singgahan, dan yang terlihat adalah kotak yang berisi
   * angka baru di sebelah kurva yang digambar dengan angka lama.
   */
  const [waterVersion, setWaterVersion] = useState(0);

  const sheet = useAsync(
    () => api.post<LrlcSheetPayload>(`/api/projects/${project.id}/lrlc/sheet/`, {
      well: wellId,
      ...request.curves,
      ...request.cutoffs,
      ...Object.fromEntries(
        Object.entries(request.archie).filter(([, v]) => v !== null && v !== undefined)),
      zone: request.zone,
      depth: depthMode,
    }),
    [project.id, wellId, signature, depthMode, waterVersion],
    { skip: !wellId },
  );

  const payload = sheet.data ?? null;
  const range = useMemo(() => (payload ? sheetRange(payload) : null), [payload]);
  const span = range ? axisSpan(range[0], range[1]) : 0;

  /**
   * Rw bukan setelan tampilan, jadi yang boleh menggesernya bukan siapa saja.
   *
   * Server menolaknya dengan 403 apa pun yang dikirim layar ini — itu yang
   * menjaganya — dan panel ini memadamkan kotaknya supaya seorang peninjau
   * tidak mengetik tiga angka lebih dulu untuk kemudian diberi tahu bahwa tak
   * satu pun tersimpan.
   */
  const mayCompute = canCompute(project.my_role);

  /**
   * Simpan rencana air sumur ini, lalu gambar ulang lembarnya.
   *
   * Dikirim pada tiap suntingan dan bukan menunggu sebuah tombol: itu yang
   * sudah berlaku di seluruh layar ini — tiap penggeser di rel kiri menghitung
   * ulang seketika — dan sebuah panel yang menahan angkanya akan memajang Rw
   * yang berbeda daripada kurva di sebelahnya selama jeda itu.
   */
  const saveWater = async (patch: WaterPlanPatch) => {
    try {
      await api.put(`/api/projects/${project.id}/lrlc/water/`,
                    { well: wellId, ...patch });
      setWaterVersion((v) => v + 1);
      onWaterChanged?.(payload?.well.name ?? "");
    } catch (error) {
      toast(error instanceof Error ? error.message
                                   : "That water could not be saved.");
    }
  };

  const {
    attachPane, scrollX, sheet: scale, scaleMode, setScaleMode,
    stretch, boxHandlers, box, consumePick,
  } = useDepthSheet({
    storageKey: "evolog.lrlc.scale",
    metres: span,
    range,
    panX: true,
  });

  /**
   * Susunan yang berlaku, sebelum satu pun setelan tampilan ditumpangkan.
   *
   * Dipegang tersendiri sebab ia pembanding, dan dipakai dua kali karenanya:
   * yang tersimpan hanyalah selisih terhadapnya — sehingga setelan yang
   * dikembalikan ke nilai asalnya benar-benar hilang alih-alih menjadi setelan
   * yang kebetulan sama — dan ia pula yang mengatakan kolom serta kurva mana
   * yang benar-benar **tergambar** pada sumur ini, yaitu satu-satunya cara
   * `toLayout` bisa membedakan yang barusan dibuang dari yang memang tidak
   * dipunyai sumurnya.
   */
  const base = useMemo<TrackSpec[]>(
    () => (payload ? buildLrlcTracks(payload, sheetTracks) : []), [payload, sheetTracks]);

  /** Susunan yang berlaku, lalu cara menggambar yang digeser pengguna di atasnya. */
  const tracks = useMemo<TrackSpec[]>(
    () => applyOverrides(base, overrides), [base, overrides]);

  /** Nama sungguhan tiap peran pada sumur yang sedang terbuka. */
  const roles = useMemo<Record<string, string>>(
    () => (payload ? resolveRoles(payload) : {}), [payload]);

  /**
   * Setelan sebagaimana rel kanan menyuntingnya — dibangun ulang tiap render.
   *
   * Susunannya ikut diserahkan, dan bukan hanya yang tergambar: kurva yang
   * dipadamkan harus tetap muncul di rel — dengan matanya tertutup — atau
   * tidak ada lagi jalan untuk menyalakannya.
   */
  const editable = useMemo<TrackConfig[]>(
    () => toTrackConfigs(tracks, sheetTracks, roles), [tracks, sheetTracks, roles]);

  /**
   * Apa yang baru disunting rel kanan, dipecah ke tempat masing-masing.
   *
   * Dua penyimpanan, dan pemisahannya disengaja: susunan — kolom apa, urutan
   * apa, kurva apa di dalamnya — ke `evolog.lrlc.layout`, dan cara
   * menggambarnya ke `evolog.lrlc.curvescale`. Yang mengembalikan warna tidak
   * membongkar susunan, dan yang mengembalikan susunan tidak menghapus warna.
   */
  const commit = (next: TrackConfig[]) => {
    setStoredLayout(toLayout(next, layout ?? defaultLayout(), roles, base));
    // Setelan yang sedang berlaku ikut, supaya yang tidak tergambar pada sumur
    // ini — kolom DT, kurva CALI, kurva yang barusan dipadamkan — tidak
    // terhapus oleh sebuah selisih yang tidak punya pembandingnya.
    setStored(diffOverrides(base, next, overrides));
  };

  /** Memindahkan satu kolom ke tempat kolom lain — dari seretan kepalanya. */
  const reorder = (id: string, targetId: string) =>
    setStoredLayout(moveEntryTo(layout ?? defaultLayout(), id, targetId));

  /**
   * Kolom bawaan yang sedang tidak ada di susunan — jalan pulang tiap buangan.
   *
   * Tanpanya tombol buang di rel kanan adalah pintu satu arah untuk separuh
   * lembar ini: keempat kolom pita tidak punya kurva untuk dibangun kembali
   * lewat "New track from curve…", dan kolom yang punya pulang tanpa arsiran,
   * sumbu logaritmik dan garis ambang yang menjadikannya kolom itu. Yang
   * tersisa dahulu hanyalah mengembalikan keenam belasnya sekaligus, yaitu
   * membuang seluruh susunan yang baru disusun demi satu kolom.
   */
  const restorable = useMemo(() => restorableTracks(layout), [layout]);

  const editCurve = (trackId: string, key: string, patch: Partial<CurveConfig>) => {
    const at = `${trackId}:${key}`;
    const curves = { ...overrides.curves, [at]: { ...overrides.curves[at], ...patch } };
    // Medan yang dikosongkan dibuang, bukan disimpan sebagai `undefined`:
    // JSON tidak membawa `undefined`, jadi menyimpannya berarti setelan yang
    // kembali sendiri sesudah halaman dimuat ulang.
    for (const field of Object.keys(curves[at]) as (keyof CurveConfig)[]) {
      if (curves[at][field] === undefined) delete curves[at][field];
    }
    if (!Object.keys(curves[at]).length) delete curves[at];
    setStored({ ...overrides, curves });
  };

  /**
   * Menukar sumbu mendatar sebuah track dari lencana LIN/LOG di kepalanya.
   *
   * Ditulis sebagai selisih terhadap `LRLC_SHEET`, bukan sebagai keadaan: track
   * RESD/RO memang logaritmik sejak awal, jadi yang tersimpan ketika seseorang
   * meluruskannya adalah "yang ini linear" — dan bukan sebuah salinan setelan
   * bawaan yang akan tertinggal pada hari susunan bawaannya berubah.
   */
  const toggleScale = (trackId: string) => {
    const original = base.find((track) => track.id === trackId);
    if (!original) return;
    const now = tracks.find((track) => track.id === trackId)?.logarithmic ?? false;
    const wanted = !now;
    const rest = { ...overrides.tracks };
    if (wanted === Boolean(original.logarithmic)) {
      const patch = { ...rest[trackId] };
      delete patch.logarithmic;
      if (Object.keys(patch).length) rest[trackId] = patch;
      else delete rest[trackId];
    } else {
      rest[trackId] = { ...rest[trackId], logarithmic: wanted };
    }
    setStored({ ...overrides, tracks: rest });
  };

  /**
   * Pita warna sebuah kolom, disunting dari kotak warna di kepalanya.
   *
   * Ditulis sebagai selisih seperti sisi tampilan lainnya — susunan bawaan
   * `LRLC_SHEET` tidak mengenal pita warna sama sekali, jadi yang tersimpan
   * adalah "kolom ini dijadikan pita". Kepalanya menyebut sendiri bahwa
   * rampnya disetel dari sana, dan sampai sekarang kalimat itu benar di lembar
   * di menu Log dan tidak benar di sini: kotaknya ada, dan tidak ada yang
   * menerima apa yang diketik ke dalamnya.
   */
  const editSpectrum = (trackId: string, patch: Partial<SpectrumConfig>) => {
    const current = editable.find((track) => track.id === trackId)?.spectrum;
    if (!current) return;
    setStored({
      ...overrides,
      tracks: {
        ...overrides.tracks,
        [trackId]: { ...overrides.tracks[trackId], spectrum: { ...current, ...patch } },
      },
    });
  };

  const removeSpectrum = (trackId: string) => {
    const patch = { ...overrides.tracks[trackId] };
    delete patch.spectrum;
    const rest = { ...overrides.tracks };
    if (Object.keys(patch).length) rest[trackId] = patch;
    else delete rest[trackId];
    setStored({ ...overrides, tracks: rest });
  };

  /**
   * Kurva yang boleh tidak ada, dan memang tidak ada pada sumur ini.
   *
   * Disebut dengan nama yang dipilih di rel kiri — "DEN", bukan "rhob" —
   * sebab itulah nama yang dicari orang di daftar kurva sumurnya. Yang tidak
   * pernah dipilih untuk proyek ini tetap disebut dengan peran yang kosong,
   * sebab ketiadaannya sama saja bagi lembar ini: tidak ada angkanya.
   *
   * Keduanya disebut "tidak terbaca" dan bukan "tidak ada pada sumur ini", dan
   * itu bukan kehati-hatian berlebih: muatan memang tidak membedakan sumur
   * yang tak pernah dilog densitas dari kurva yang barusan dimatikan sendiri
   * di rel kiri layar LRLC — lihat `absent` di `LrlcSheetView` — dan sebuah
   * kalimat yang menyalahkan sumurnya untuk pilihan yang baru saja dibuat
   * orangnya mengirimnya memeriksa data yang tidak bermasalah.
   *
   * Dipajang, dan tidak hanya dipakai membuang kolom. Sebuah lembar yang
   * tiba-tiba berkolom dua belas alih-alih enam belas terbaca sebagai susunan
   * yang rusak — atau lebih buruk, tidak terbaca sama sekali — sedangkan satu
   * kalimat di kepalanya menjadikan kolom yang hilang sebuah keterangan
   * tentang sumurnya.
   */
  const missing = useMemo<string[]>(() => {
    if (!payload?.absent?.length) return [];
    return payload.absent.map((key) => payload.names?.[key] || key.toUpperCase());
  }, [payload]);

  const touched =
    Object.keys(overrides.tracks).length + Object.keys(overrides.curves).length;
  const rearranged = !isDefaultLayout(layout);

  const reading = pinned ?? cursor;

  const readout = useMemo(() => {
    if (reading === null) return [];
    const seen = new Set<string>();
    const rows: { name: string; value: number; unit?: string }[] = [];
    for (const track of tracks) {
      for (const curve of track.curves) {
        if (seen.has(curve.name)) continue;
        const value = sampleAt(curve, reading);
        if (value === null) continue;
        seen.add(curve.name);
        rows.push({ name: curve.name, value, unit: curve.unit });
      }
    }
    return rows;
  }, [reading, tracks]);

  /** Kelas dan uji produksi di kedalaman yang sedang dibaca. */
  const at = useMemo(() => {
    if (!payload || reading === null) return null;
    const band = payload.bands.find((b) => reading >= b.top && reading <= b.base);
    const test = payload.tests.find((t) => reading >= t.top && reading <= t.base);
    return { band: band ?? null, test: test ?? null };
  }, [payload, reading]);

  const zones = payload?.well.zones ?? [];
  const markers = payload?.well.markers ?? [];

  const banding = (kind: BandKind | undefined) =>
    payload && kind ? bandsFor(payload, kind) : undefined;

  /**
   * Apa yang dibaca kotak pembaca: satu kolom, pada kedalaman di bawah jari.
   *
   * Kolom yang ditunjuk saja dan bukan keenam belasnya — yang keenam belas
   * sudah ada di kaki lembar, dan sebuah kotak melayang setinggi dua puluh
   * baris menutupi justru bagian lembar yang sedang ditunjuk. Kolom pita
   * melaporkan nama pitanya alih-alih angka: "LRLC — potential" adalah
   * seluruh isi kolom itu pada kedalaman tersebut, dan sebuah kolom yang
   * tidak melaporkan apa pun terbaca seperti pembacaan yang gagal.
   *
   * Padam ketika sebuah kedalaman sedang dipaku: paku adalah pembacaan yang
   * dibekukan, dan kotak yang mengikuti jari sambil memajang angka dari
   * tempat lain adalah dua pembacaan di satu layar.
   */
  const picker = useMemo(() => {
    // `Number.isFinite` dan bukan sekadar `!== null`: kedalaman datang dari
    // pembagian dengan tinggi kotak track, dan kotak setinggi nol — track yang
    // belum ditata, atau jendela yang sedang dilipat — memulangkan NaN. Sebuah
    // kotak pembaca yang memajang "—" di atas kolom yang jelas berisi kurva
    // terbaca seperti data yang hilang.
    if (!pointer || pinned !== null || cursor === null || !Number.isFinite(cursor) || box) {
      return null;
    }
    const track = tracks.find((item) => item.id === pointer.track);
    if (!track) return null;
    const rows = track.curves
      .map((curve) => ({ name: curve.name, unit: curve.unit, value: sampleAt(curve, cursor) }))
      .filter((row): row is { name: string; unit: string | undefined; value: number } =>
        row.value !== null && Number.isFinite(row.value));
    const kind = track.bands as BandKind | undefined;
    const band = kind
      ? banding(kind)?.find((item) => cursor >= item.top && cursor <= item.base) ?? null
      : null;
    return { title: track.title, rows, band, note: kind ? emptyBandNote(kind) : "" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointer, pinned, cursor, box, tracks, payload]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 110,
        background: "var(--app-bg)", display: "flex", flexDirection: "column",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="LRLC log sheet"
    >
      <header
        style={{
          display: "flex", alignItems: "center", gap: 9, flex: "none",
          padding: "7px 11px", background: "var(--toolbar)",
          borderBottom: "1px solid var(--line-strong)",
        }}
      >
        <span style={{ font: "600 12px var(--font-sans)", color: "var(--ink)" }}>
          LRLC log sheet
        </span>
        <Dropdown
          value={String(wellId)}
          options={wells.map((w) => ({ value: String(w.id), label: w.name }))}
          onChange={(value) => setWellId(Number(value))}
          width={150}
          compact
        />
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {range
            ? `${payload?.depth_label ?? "MD"} ${units.formatDepth(range[0])}–${units.formatDepth(range[1])}`
            : "—"} · scale 1:{scale.scale}{scale.fitted ? " (fit)" : ""}
          {payload && ` · ${payload.interval}`}
          {payload && payload.drawn < payload.samples
            && ` · ${payload.drawn.toLocaleString("en-US")} of ${payload.samples.toLocaleString("en-US")} samples drawn`}
          {payload && payload.depth_reference !== "md" && payload.depth_source !== "survey"
            && " · no survey: assumed vertical"}
          {payload?.depth_caveat && " · TVD from the rotary table: no ground elevation"}
          {missing.length > 0 && ` · drawn without ${missing.join(", ")}`}
        </span>
        <span style={{ flex: 1 }} />
        {/* `Toggle` selebar panel yang memuatnya; di dalam sebuah toolbar ia
            harus dikurung, atau ia memakan seluruh sisa baris. */}
        <div style={{ width: 78, flex: "none" }}>
          <Toggle checked={showGrid} onChange={setShowGrid} label="Grid" />
        </div>
        <div style={{ width: 104, flex: "none" }}>
          <Toggle checked={showReadings} onChange={setShowReadings} label="Readings" />
        </div>
        <div style={{ width: 96, flex: "none" }}>
          <Toggle checked={showMarkers} onChange={setShowMarkers} label="Markers" />
        </div>
        <Dropdown
          value={depthMode}
          options={DEPTH_ORDER.map((value) => ({
            value, label: DEPTH_LABEL[value], hint: DEPTH_HINT[value],
          }))}
          onChange={(value) => setDepthMode(value as DepthReference)}
          width={92}
          compact
        />
        <Button variant="ghost" title="Stretch vertically (mouse wheel over the sheet)"
                onClick={() => setScaleMode(stepScale(scale.scale, 1))}>+</Button>
        <Button variant="ghost" title="Compress vertically (mouse wheel over the sheet)"
                onClick={() => setScaleMode(stepScale(scale.scale, -1))}>−</Button>
        <Button variant="ghost" title="Fit the whole well into the panel height"
                onClick={() => setScaleMode("fit")}>Fit</Button>
        <Dropdown
          value={scaleMode === "fit" ? "fit" : String(scale.scale)}
          options={[
            { value: "fit", label: "Fit" },
            ...[...new Set([...DEPTH_SCALE_PRESETS, ...(scale.fitted ? [] : [scale.scale])])]
              .sort((a, b) => a - b)
              .map((step) => ({ value: String(step), label: `1:${step}` })),
          ]}
          onChange={(value) => setScaleMode(value === "fit" ? "fit" : Number(value))}
          width={84}
          compact
        />
        <Button
          variant="ghost"
          title="Put every track back on the sheet's own colours and scales"
          disabled={!touched}
          onClick={() => {
            setStored({ tracks: {}, curves: {} });
            toast("Every track is back on the sheet's own settings.");
          }}
        >
          Reset display
        </Button>
        <Button
          variant={railOpen ? "primary" : "ghost"}
          title={railOpen
            ? "Close the sheet setup"
            : "Rw and the RT/RO cutoff, the columns and their order, and how each one is drawn"}
          onClick={() => setRailOpen(!railOpen)}
        >
          {Icon.curve({ size: 11 })} Tracks
          {touched || rearranged
            ? ` · ${touched + (rearranged ? 1 : 0)}`
            : ""}
        </Button>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </header>

      {payload && <ClassLegend payload={payload} />}

      {sheet.loading && !payload && <Loading label="Classifying and drawing…" />}
      {sheet.error && (
        <div style={{ padding: 16 }}><Notice tone="error">{sheet.error}</Notice></div>
      )}
      {!sheet.loading && !sheet.error && payload && !range && (
        <Empty>Nothing to draw: no sample carries every curve this reads.</Empty>
      )}

      {payload && range && (
        <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "stretch" }}>
        {tracks.length === 0 ? (
          /* Lembar yang kolom terakhirnya barusan dibuang.
             Digantung di sini dan bukan di sekeliling seluruh baris, sebab
             rel kanan harus tetap berdiri: ia yang memuat "+ New track" dan
             "Restore the default layout", dan sebuah tombol buang yang
             membawa serta jalan pulangnya adalah pintu satu arah. Lembar di
             menu Log menempatkan relnya di luar saringan yang sama. */
          <div style={{ flex: 1, minWidth: 0, display: "flex",
                        alignItems: "center", justifyContent: "center" }}>
            <Empty>
              Every column has been taken out of this sheet.
              <span style={{ font: "400 11px var(--font-mono)" }}>
                Add one from the sheet setup, or take the default sixteen back.
              </span>
              <Button onClick={() => { setStoredLayout(null); setRailOpen(true); }}>
                Restore the default columns
              </Button>
            </Empty>
          </div>
        ) : (
        <div style={{
          flex: 1, minWidth: 0, minHeight: 0,
          display: "flex", flexDirection: "column",
          // Kepala track digeser mendatar oleh `scrollX` alih-alih memiliki
          // batang gulirnya sendiri, jadi ia meluap keluar kolomnya menurut
          // rancangannya. Tanpa pengguntingan di sini, keenam belas kepala itu
          // terus tergambar di bawah rel kanan dan menumpuk dengan isinya.
          overflow: "hidden",
        }}>
          <TrackHeaders
            depthUnit={`${payload.depth_label} ${units.depthUnit}`}
            tracks={tracks}
            gridDivisions={gridDivisions}
            weight={weight}
            scrollX={scrollX}
            selected={selectedTrack}
            // Mengeklik kepala track membuka panelnya di rel kanan, sebagaimana
            // di menu Log — dan membuka relnya kalau masih tertutup, sebab
            // sebuah pilihan yang tidak memperlihatkan apa pun terbaca seperti
            // klik yang tidak mendarat.
            onSelect={(id) => {
              setSelectedTrack(id === selectedTrack ? null : id);
              if (id !== selectedTrack) setRailOpen(true);
            }}
            onScaleToggle={toggleScale}
            onReorder={reorder}
            onCurveEdit={editCurve}
            onSpectrumEdit={editSpectrum}
            onSpectrumRemove={removeSpectrum}
            onCurveAuto={(trackId, key) =>
              editCurve(trackId, key, { min: undefined, max: undefined })}
          />
          <div
            ref={attachPane}
            className="scroll-y"
            style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}
          >
            <div
              style={{
                display: "flex", alignItems: "stretch",
                height: scale.fitted ? "100%" : scale.heightPx,
                minWidth: DEPTH_GUTTER_W + tracksMinWidth(tracks),
              }}
            >
              <DepthGutter
                top={range[0]}
                base={range[1]}
                ticks={depthTickCount(scale.heightPx)}
                cursor={reading}
                cursorLabel={(depth) => units.formatDepth(depth)}
                onStretch={stretch}
                style={{ position: "sticky", left: 0, zIndex: 2 }}
              />
              <div
                ref={sheetRef}
                {...boxHandlers}
                // Kedudukan jari dan kolom yang ditunjuknya, untuk kotak
                // pembaca di bawah. Dibaca dari `data-track-id` — penanda yang
                // sudah dipasang tiap kolom, dan yang juga dibaca menu
                // klik-kanan lembar di menu Log — alih-alih dihitung dari
                // lebar tiap kolom: lebar itu bergeser bersama `flex` yang
                // boleh disetel, dan yang menghitungnya sendiri akan menunjuk
                // kolom sebelah pada lembar yang salah satu kolomnya
                // dilebarkan.
                onMouseMove={(event) => {
                  const column = (event.target as HTMLElement)
                    .closest?.("[data-track-id]")?.getAttribute("data-track-id");
                  setPointer(column
                    ? { x: event.clientX, y: event.clientY, track: column }
                    : null);
                }}
                onMouseLeave={() => setPointer(null)}
                style={{
                  position: "relative", flex: 1, minWidth: 0,
                  display: "flex", alignItems: "stretch",
                  touchAction: box ? "none" : undefined,
                  userSelect: box ? "none" : undefined,
                }}
              >
                {tracks.map((track) => (
                  <LogTrack
                    key={track.id}
                    track={track}
                    top={range[0]}
                    base={range[1]}
                    zones={zones.map((z) => ({
                      name: z.name, top: z.top_md, base: z.base_md, color: z.color }))}
                    markers={showMarkers
                      ? markers.map((m) => ({ name: m.name, md: m.md, color: m.color }))
                      : []}
                    showGrid={showGrid}
                    gridDivisions={gridDivisions}
                    depthGrid={depthGrid}
                    weight={weight}
                    classBlocks={banding(track.bands as BandKind | undefined)}
                    emptyNote={emptyBandNote(track.bands as BandKind | undefined)}
                    onHoverDepth={setCursor}
                    cursorDepth={reading}
                    showReadings={showReadings}
                    onPickDepth={(depth) => {
                      if (consumePick()) return;
                      setPinned((current) =>
                        current !== null && Math.abs(current - depth) * scale.pxPerMetre < 4
                          ? null : depth);
                    }}
                  />
                ))}
                {/* Pegangan marker, dari lembar di menu Log dan bukan salinannya.
                    Garis putus-putus di dalam tiap kolom mengatakan "ada
                    sesuatu di kedalaman ini" dan tidak pernah mengatakan apa;
                    yang menyebut namanya adalah lapisan ini.

                    `mayWrite` selalu salah di sini, dan itu bukan kelalaian:
                    lembar ini dibuka untuk memeriksa sebuah penggolongan, dan
                    sebuah seretan yang memindahkan top sungguhan di dalam
                    proyek sambil orangnya memeriksa ambang adalah suntingan
                    yang tidak pernah diniatkan. Yang memindahkan top
                    melakukannya di layar Log atau di layar korelasi, tempat
                    pekerjaan itu memang berlangsung. */}
                {showMarkers && markers.length > 0 && (
                  <MarkerHandles
                    markers={markers.map((m) => ({
                      id: m.id, name: m.name, md: m.md, color: m.color }))}
                    top={range[0]}
                    base={range[1]}
                    mayWrite={false}
                    onDrag={() => {}}
                    onMenu={() => {}}
                  />
                )}
                {box && (
                  <BoxZoomOverlay
                    from={box.from}
                    to={box.to}
                    height={sheetRef.current?.clientHeight ?? 1}
                    top={range[0]}
                    base={range[1]}
                    format={(depth) => units.formatDepth(depth)}
                  />
                )}
              </div>
            </div>
          </div>

          <footer
            style={{
              display: "flex", alignItems: "center", gap: 14, height: 26, flex: "none",
              padding: "0 11px", background: "var(--toolbar)",
              borderTop: "1px solid var(--line)",
              font: "400 10px var(--font-mono)", color: "var(--ink-4)",
              overflowX: "auto", whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: pinned !== null ? "var(--accent)" : undefined }}>
              {pinned !== null ? "pinned" : "cursor"}{" "}
              {reading !== null ? units.formatDepth(reading) : "—"}
            </span>
            {at?.band && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2,
                               background: at.band.color, flex: "none" }} />
                {at.band.label}
              </span>
            )}
            {at?.test && (
              <span style={{ color: at.test.color }}>
                {at.test.kind_label} — {at.test.fluid_label}
                {at.test.rate ? ` · ${at.test.rate}` : ""}
              </span>
            )}
            {readout.map((row) => (
              <span key={row.name}>
                {row.name} {Number.isFinite(row.value) ? row.value.toPrecision(4) : "—"}
                {row.unit ? ` ${row.unit}` : ""}
              </span>
            ))}
            <span style={{ flex: 1 }} />
            <span>
              a {payload.archie.a.toFixed(2)} · m {payload.archie.m.toFixed(2)}
              {" · "}Rw {payload.archie.rw.toFixed(4)}
            </span>
          </footer>
        </div>
        )}

        {railOpen && (
          <aside
            style={{
              width: 274, flex: "none", display: "flex", flexDirection: "column",
              minHeight: 0, background: "var(--panel)",
              borderLeft: "1px solid var(--line-strong)",
            }}
          >
            <div
              style={{
                display: "flex", alignItems: "center", gap: 8, flex: "none",
                height: 30, padding: "0 10px", background: "var(--toolbar)",
                borderBottom: "1px solid var(--line)",
                font: "600 10px var(--font-mono)", letterSpacing: ".06em",
                color: "var(--ink-3)", textTransform: "uppercase",
              }}
            >
              Sheet setup
              <span style={{ flex: 1 }} />
              <Button variant="ghost" onClick={() => setRailOpen(false)}>
                Close
              </Button>
            </div>
            <div className="scroll-y" style={{ flex: 1, minHeight: 0, overflowY: "auto",
                                               padding: 9, display: "flex",
                                               flexDirection: "column", gap: 9 }}>
              {payload.water_plan && (
                <LrlcWaterPlanPanel
                  plan={payload.water_plan}
                  wellName={payload.well.name}
                  readOnly={!mayCompute}
                  onChange={saveWater}
                />
              )}
              {missing.length > 0 && (
                <Notice>
                  {missing.join(", ")} {missing.length > 1 ? "are" : "is"} not
                  read here — either this well was never logged for
                  {missing.length > 1 ? " them" : " it"}, or
                  {missing.length > 1 ? " they were" : " it was"} left out on
                  the LRLC screen — so the columns that read
                  {missing.length > 1 ? " them" : " it"} are not drawn, and the
                  ones that read {missing.length > 1 ? "them" : "it"} alongside
                  something else name only what is under them. Nothing was
                  removed from the layout: the next well that carries
                  {missing.length > 1 ? " those curves" : " that curve"} gets
                  those columns back where they were.
                </Notice>
              )}
              <Notice>
                The order these columns run in is the sheet's argument, from
                what is easiest to miscalibrate to what is hardest. Move,
                remove and add them as the question in front of you needs — a
                column taken out comes back on its own, and the default sixteen
                are one button away, both at the foot of this rail.
              </Notice>
              <TrackEditor
                layout={{
                  onRestore: () => setStoredLayout(null),
                  restorable,
                  onRestoreOne: (id) =>
                    setStoredLayout(restoreEntry(layout ?? defaultLayout(), id)),
                }}
                tracks={editable}
                onChange={commit}
                resolved={tracks}
                available={payload.curves}
                selected={selectedTrack}
                onSelect={setSelectedTrack}
              />
              <Panel title="Display" bodyStyle={{ padding: 11, gap: 12 }}>
                <Toggle label="Grid lines" checked={showGrid} onChange={setShowGrid} />
                <Toggle label="Well markers" checked={showMarkers} onChange={setShowMarkers} />
                <Slider
                  label="Grid columns" value={gridDivisions} min={2} max={10} step={1}
                  onChange={(value) => setGridDivisions(Math.round(value))}
                  format={(value) => `${Math.round(value)} per track`}
                />
                <Slider
                  label="Grid rows" value={depthGrid} min={8} max={64} step={2}
                  onChange={(value) => setDepthGrid(Math.round(value))}
                  format={(value) => `${Math.round(value)} px`}
                />
                <Toggle label="Picker readings on the track"
                        checked={showReadings} onChange={setShowReadings} />
                <Slider
                  label="Ketebalan bawaan" value={weight} min={0.8} max={3} step={0.1}
                  onChange={setWeight} format={(v) => `${v.toFixed(1)} px`}
                />
              </Panel>
            </div>
          </aside>
        )}
        </div>
      )}

      {/* Kotak pembaca, mengikuti jari.
          `fixed` dan bukan `absolute`: ia harus bisa keluar dari kolom yang
          menggunting isinya, dan kolom lembar ini selebar 60 piksel. Dibalik
          ke kiri dan ke atas di dekat tepi layar, sebab kotak yang separuhnya
          di luar jendela tidak memberi tahu apa pun. `pointerEvents: none`
          supaya ia tidak pernah menjadi sasaran mouse yang sedang melacaknya
          — tanpa itu, kotaknya sendiri yang memutus pelacakan. */}
      {picker && pointer && cursor !== null && (
        <div
          role="status"
          aria-label="Reading under the cursor"
          style={{
            position: "fixed",
            left: pointer.x + (pointer.x > window.innerWidth - 230 ? -218 : 14),
            top: pointer.y + (pointer.y > window.innerHeight - 190 ? -14 - 170 : 14),
            zIndex: 130,
            pointerEvents: "none",
            minWidth: 150,
            maxWidth: 204,
            padding: "5px 7px",
            background: "var(--panel)",
            border: "1px solid var(--line-strong)",
            borderRadius: 5,
            boxShadow: "var(--shadow-float)",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ font: "600 10.5px var(--font-mono)", color: "var(--accent)" }}>
              {units.formatDepth(cursor)}
            </span>
            <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)",
                           overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {picker.title}
            </span>
          </div>
          {picker.rows.map((row) => (
            <div key={row.name} style={{ display: "flex", alignItems: "baseline", gap: 6,
                                         font: "500 10px var(--font-mono)", color: "var(--ink-2)" }}>
              <span style={{ color: "var(--ink-5)", flex: 1, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.name}
              </span>
              <span>{row.value.toPrecision(4)}</span>
              {row.unit && (
                <span style={{ color: "var(--ink-6)", font: "400 9px var(--font-mono)" }}>
                  {row.unit}
                </span>
              )}
            </div>
          ))}
          {picker.band && (
            <div style={{ display: "flex", alignItems: "center", gap: 5,
                          font: "500 9.5px var(--font-sans)", color: "var(--ink-2)" }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, flex: "none",
                             background: picker.band.fill }} />
              {picker.band.label}
            </div>
          )}
          {!picker.rows.length && !picker.band && (
            <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)" }}>
              {picker.note || "Nothing at this depth"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Legenda */

/**
 * Kepala lembar: ketujuh kelas, dan pengelompokan yang membentang di atasnya.
 *
 * Dua baris pita di atas swatch-nya, dan itu bukan hiasan. "Net reservoir" dan
 * "pay" bukan kelas kedelapan dan kesembilan — keduanya **himpunan** dari
 * kelas yang sudah ada, dan sebuah legenda yang mencetak keduanya sebagai
 * kotak warna sejajar dengan yang lain mengajarkan hal yang salah tentang apa
 * yang dihitung. Pita yang membentang memperlihatkan bahwa air ikut reservoir
 * dan tidak ikut pay, dan itu justru yang paling sering ditanyakan orang.
 *
 * Rentangnya dihitung dari `groups` yang datang bersama muatannya, bukan
 * ditulis di sini. Nomor kelas milik `petro/lrlc.py`, dan sebuah rentang yang
 * disalin dengan tangan adalah rentang yang akan tertinggal ketika kelas
 * kedelapan lahir.
 */
function ClassLegend({ payload }: { payload: LrlcSheetPayload }) {
  const order = payload.classes.map((c) => c.code);
  const thickness = new Map(payload.rows.map((row) => [row.code, row.thickness]));

  const span = (codes: number[]): [number, number] | null => {
    const columns = codes.map((code) => order.indexOf(code)).filter((i) => i >= 0);
    if (!columns.length) return null;
    return [Math.min(...columns) + 1, Math.max(...columns) + 2];
  };

  const bars: { label: string; codes: number[]; row: number; tone: string }[] = [
    { label: "NET RESERVOIRS", codes: payload.groups.reservoir, row: 1,
      tone: payload.reservoir_color },
    { label: "PAY RESERVOIRS", codes: payload.groups.pay, row: 2,
      tone: payload.pay_color },
    { label: "LOW RESISTIVITY", codes: payload.groups.low_resistivity, row: 3,
      tone: "#a020f0" },
    { label: "NON LOW RESISTIVITY",
      codes: [...payload.groups.low_contrast, ...payload.groups.non_low_contrast],
      row: 3, tone: "var(--ink-5)" },
  ];

  return (
    <div
      style={{
        flex: "none", padding: "6px 11px 7px",
        borderBottom: "1px solid var(--line)", background: "var(--panel)",
        overflowX: "auto",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${order.length}, minmax(104px, 1fr))`,
          gap: "3px 4px",
          minWidth: order.length * 104,
        }}
      >
        {bars.map((bar) => {
          const at = span(bar.codes);
          if (!at) return null;
          return (
            <div
              key={bar.label}
              style={{
                gridRow: bar.row, gridColumn: `${at[0]} / ${at[1]}`,
                height: 13, borderRadius: 3,
                border: `1px solid ${bar.tone}`,
                background: "var(--panel-inset)",
                display: "flex", alignItems: "center", justifyContent: "center",
                font: "600 8px/1 var(--font-mono)", letterSpacing: ".04em",
                // Garisnya memakai warna pitanya apa adanya; tulisannya tidak.
                // Dua dari empat warna ini kini dipilih tiap proyek, dan warna
                // yang bagus sebagai isi kolom selebar dua puluh piksel belum
                // tentu terbaca sebagai huruf setinggi delapan: kuning muda
                // bawaan RES di atas panel yang nyaris putih adalah tulisan
                // yang harus dicari, bukan dibaca.
                //
                // Dicampur ke `--ink-2` dan bukan digelapkan, sebab lembar ini
                // dibaca pada kedua tema: yang menggelapkan akan membetulkan
                // tema terang sambil mematikan tema gelap, tempat justru warna
                // tua yang menghilang. `--ink-2` adalah tinta utama masing-
                // masing tema, jadi campurannya selalu bergerak ke arah yang
                // terbaca — dan rona pitanya tetap kelihatan.
                color: `color-mix(in oklab, ${bar.tone}, var(--ink-2) 40%)`,
                whiteSpace: "nowrap", overflow: "hidden",
              }}
            >
              {bar.label}
            </div>
          );
        })}

        {payload.classes.map((spec, index) => {
          const metres = thickness.get(spec.code) ?? 0;
          return (
            <div
              key={spec.code}
              title={`Class ${spec.code} — ${spec.label}`}
              style={{
                gridRow: 4, gridColumn: index + 1,
                display: "flex", alignItems: "center", gap: 5,
                padding: "2px 5px", borderRadius: 3,
                background: "var(--panel-inset)",
                border: "1px solid var(--line)",
                opacity: metres > 0 ? 1 : 0.45,
              }}
            >
              <span style={{
                width: 11, height: 11, borderRadius: 2, flex: "none",
                background: spec.color, border: "1px solid rgba(0,0,0,.28)",
              }} />
              <span style={{
                font: "500 9px var(--font-mono)", color: "var(--ink-3)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {spec.code} · {spec.label}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-5)" }}>
                {metres.toFixed(1)} m
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

